import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ProductFactsModel, ShelfLifeUnit, UiLabel } from 'hx-services';

/**
 * «Для сайта: состав и пищевая ценность» of the product form: the pure part — limits, validators and the mapping
 * between the model (`ProductInfoModel.facts`) and the values of the form controls. No Angular component in here,
 * so that the Karma spec runs it as it is.
 */

/** The same limits the server checks (vn `ProductInfoResource.validateFacts`); the form shows them as visible errors, never clamps. */
export const FACTS_LIMITS = {
  kcal: { min: 0, max: 900 },
  macro: { min: 0, max: 100 },
  storageMaxTemp: { min: -30, max: 30 },
  shelfLife: { min: 1, max: 9999 },
  ingredientsLength: 2000,
  storageConditionsLength: 500,
} as const;

/** What the nine controls of the facts group hold. Numbers are numbers (never strings), «nothing» is always `null`. */
export interface ProductFactsFormValue {
  kcal: number | null;
  protein: number | null;
  fat: number | null;
  carbs: number | null;
  ingredients: UiLabel | null;
  storageConditions: UiLabel | null;
  storageMaxTemp: number | null;
  shelfLife: number | null;
  shelfLifeUnit: ShelfLifeUnit | null;
}

export function emptyFactsForm(): ProductFactsFormValue {
  return {
    kcal: null,
    protein: null,
    fat: null,
    carbs: null,
    ingredients: null,
    storageConditions: null,
    storageMaxTemp: null,
    shelfLife: null,
    shelfLifeUnit: null,
  };
}

/**
 * Model → form values. Always returns all nine keys (`null` for what is not filled), so that `reset` with the result
 * clears a stale value when the loaded product has no facts at all.
 */
export function factsToForm(facts: ProductFactsModel | null | undefined): ProductFactsFormValue {
  if (!facts) {
    return emptyFactsForm();
  }
  return {
    kcal: numberOrNull(facts.kcal),
    protein: numberOrNull(facts.protein),
    fat: numberOrNull(facts.fat),
    carbs: numberOrNull(facts.carbs),
    ingredients: normalizeLabel(facts.ingredients),
    storageConditions: normalizeLabel(facts.storageConditions),
    storageMaxTemp: numberOrNull(facts.storageMaxTemp),
    shelfLife: numberOrNull(facts.shelfLife),
    shelfLifeUnit: unitOrNull(facts.shelfLifeUnit),
  };
}

/**
 * Form values → the `facts` of the save request. All nine keys are sent (the server replaces everything, `null` clears):
 * a text blank in every language → `null`; a unit without a number → `null`; kcal/protein/fat/carbs to one decimal.
 * A number without a unit is NOT repaired here (no default unit): the form validator stops it before this is called.
 */
export function factsFromForm(value: Partial<ProductFactsFormValue>): ProductFactsModel {
  const shelfLife = numberOrNull(value.shelfLife);
  return {
    kcal: roundToTenth(numberOrNull(value.kcal)),
    protein: roundToTenth(numberOrNull(value.protein)),
    fat: roundToTenth(numberOrNull(value.fat)),
    carbs: roundToTenth(numberOrNull(value.carbs)),
    ingredients: normalizeLabel(value.ingredients),
    storageConditions: normalizeLabel(value.storageConditions),
    storageMaxTemp: numberOrNull(value.storageMaxTemp),
    shelfLife,
    shelfLifeUnit: shelfLife === null ? null : unitOrNull(value.shelfLifeUnit),
  };
}

/** Each language trimmed; a language without text is dropped; nothing left in any language → `null` (never `{}`, never `{ru: ''}`). */
export function normalizeLabel(label: UiLabel | null | undefined): UiLabel | null {
  if (!label) {
    return null;
  }
  const texts: Record<string, string> = {};
  for (const [lang, text] of Object.entries(label)) {
    const trimmed = typeof text === 'string' ? text.trim() : '';
    if (trimmed) {
      texts[lang] = trimmed;
    }
  }
  return Object.keys(texts).length > 0 ? (texts as UiLabel) : null;
}

/** A finite number from a number or a numeric string; anything else (blank, NaN, text) is `null`. */
export function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim().replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Same rule as the server: HALF_UP to one decimal, 20.77 → 20.8, 364 → 364. */
export function roundToTenth(value: number | null): number | null {
  return value === null ? null : Math.round(value * 10) / 10;
}

function unitOrNull(value: unknown): ShelfLifeUnit | null {
  return value === 'HOURS' || value === 'DAYS' ? value : null;
}

/**
 * `nzParser` of the number inputs: the default one stops at «20,7» (NaN — the digits after the comma are lost, on blur the value
 * falls back to «20»), and the help text promises a comma. Typed commas are turned into dots by `DecimalCommaDirective`;
 * this is for what is pasted.
 */
export function parseDecimal(text: string): number {
  return +text.trim().replace(/[。,]/g, '.');
}

/** `nzFormatter` of the one-decimal inputs: «364», «20.7» — not «364.0» (with `nzPrecision` the input would always show the decimal). */
export function formatDecimal(value: number): string {
  return String(value);
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * A number between `min` and `max` (both inclusive); blank is fine (the field is optional). With `integer` also no fraction.
 * The error is always `range` — the form shows one text per control.
 */
export function rangeValidator(min: number, max: number, integer = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isBlank(control.value)) {
      return null;
    }
    const value = numberOrNull(control.value);
    const ok = value !== null && value >= min && value <= max && (!integer || Number.isInteger(value));
    return ok ? null : { range: { min, max, integer } };
  };
}

/** A text (`UiLabel`): no language longer than `max` characters (trimmed — like the server counts it). */
export function labelMaxLengthValidator(max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const label = control.value as UiLabel | null | undefined;
    if (!label) {
      return null;
    }
    const tooLong = Object.values(label).some(text => typeof text === 'string' && text.trim().length > max);
    return tooLong ? { maxlength: { max } } : null;
  };
}

/** For the unit control: a number of shelf life is set — the unit must be chosen. A unit alone is fine (it is ignored). */
export function shelfLifeUnitRequired(control: AbstractControl): ValidationErrors | null {
  const shelfLife = numberOrNull(control.parent?.get('shelfLife')?.value);
  return shelfLife !== null && isBlank(control.value) ? { unitRequired: true } : null;
}
